import React from "react";
import { MemoryRouter } from "react-router-dom";
//Material imports
import { ThemeProvider } from "@material-ui/core/styles";
//Limitless imports
import { ClientContext, log } from "@limitless/common/utils";
import { useTranslation, useResources } from "@limitless/common/hooks";
import { shouldBlockAppEntry } from "@limitless/customer/utils";
import { useCustomerContext, useCustomerMessageContext } from "@limitless/customer/hooks";
import { createTheme } from "@limitless/customer/theme/Theme";
import Routes from "./routes/Routes";
import Fab from "./components/fab/Fab";
import ReachOutExperience from "./components/reach-out-experience/ReachOutExperience";
import { useAppVisibilityContext } from "./hooks/app-visibility/AppVisibilityContext";
import { useMobileDetectionContext } from "./hooks/mobile-detection/MobileDetectionContext";
import { useReachOutContext } from "./hooks/reach-out/ReachOutContext";

const LiveMessenger = () => {
    const { appVisibility, toggleAppOpen, unReadMessage, inputFocus } = useAppVisibilityContext();
    const { isMobileDevice } = useMobileDetectionContext();
    const {
        customer: { emailViewed }
    } = useCustomerContext();
    const { message } = useCustomerMessageContext();
    const {
        resources: { upgradeContent, uilabels, theme, loading }
    } = useResources();
    const { setResourceBundle } = useTranslation();
    const { isReachOutMode } = useReachOutContext();

    const [llmState, setllmState] = React.useState({
        fromEmail: false,
        sessionMessage: null
    });

    const { isAppVisible, isAppOpen } = appVisibility;

    React.useEffect(() => {
        const newState = {};
        //check the window query parameters to see if this came from an email
        const fromEmail = ClientContext.validateQueryParameters("messageId", "accessKey");
        //force the LLM window if we've come from an email
        if (fromEmail) {
            newState.fromEmail = true;
            toggleAppOpen();
        }
        log.debug("New State ", newState);
        setllmState({ ...llmState, ...newState });
    }, []);

    React.useEffect(() => {
        if (!loading) {
            setResourceBundle(uilabels);
        }
    }, [loading]);

    React.useEffect(() => {
        if (emailViewed && llmState.fromEmail) {
            setllmState({ ...llmState, fromEmail: false });
        }
    }, [emailViewed]);

    const handleFABClick = () => {
        //check to see if we have a session message
        if (message.accessKey) {
            const sessionMessage = { messageId: message.messageId, accessKey: message.accessKey };
            setllmState({ ...llmState, sessionMessage });
        }

        toggleAppOpen();
    };

    //this was just for Android - a total redux hack
    const anchorStyles = {};
    //llm-anchor provides styles sticky position for smooth mobile scrolling of Fab, this needs to be changed to fixed when LLM launches
    if (isAppVisible) {
        anchorStyles.position = "fixed";
    }

    if (isAppOpen && isMobileDevice) {
        anchorStyles.width = "100vw";
        anchorStyles.height = window.innerHeight;
    }

    if (inputFocus && isMobileDevice) {
        anchorStyles.height = "100%";
    }

    const maintenanceContent = shouldBlockAppEntry("web", upgradeContent);

    if (maintenanceContent.osContent && maintenanceContent.osContent.hideLLM === true) {
        //eslint-disable-next-line
        return <div data-llm-maintenance={true} />;
    }

    const { fromEmail, sessionMessage } = llmState;

    return (
        <div>
            {!loading && isAppVisible && (
                <ThemeProvider theme={createTheme(theme)}>
                    <div className="llm-anchor" style={anchorStyles}>
                        {isAppOpen && (
                            <div className="llm-shadow">
                                {/* MemoryRouter lets us have navigation without updating the address bar */}
                                <MemoryRouter>
                                    <Routes
                                        fromEmail={fromEmail}
                                        sessionMessage={sessionMessage}
                                        maintenanceContent={maintenanceContent}
                                    />
                                </MemoryRouter>
                            </div>
                        )}
                        <Fab
                            isLLMVisible={isAppOpen}
                            isMobileDevice={isMobileDevice}
                            onClick={handleFABClick}
                            unReadMessage={unReadMessage && !isAppOpen}
                        />
                    </div>
                </ThemeProvider>
            )}
            {!loading && isReachOutMode && <ReachOutExperience onFABClick={handleFABClick} />}
        </div>
    );
};

export default LiveMessenger;
